import type { QueryClient } from '@tanstack/react-query'
import { activity } from './activity'
import { APP_BUILD } from './build'
import { createChunkErrorHandler, installChunkErrorListeners, type ChunkErrorHandler } from './chunk-errors'
import { drafts } from './drafts'
import { watchIdentity } from './identity'
import { createSwApi, registerServiceWorker, swSupported } from './sw'
import { createUpdateManager, reloadFlagKey, type ChannelLike, type UpdateManager } from './update-manager'
import { checkVersion, createVersionChecker, type CheckResult, type VersionChecker } from './version'

export interface PwaRuntime {
  manager: UpdateManager
  checker: VersionChecker
  chunk: ChunkErrorHandler
  start(): () => void
}

let current: PwaRuntime | null = null

/** Chunk errors that happen before the runtime is mounted (first route chunk) wait here. */
const early: unknown[] = []

/** For route/lazy errors surfaced outside window events (e.g. router state errors). */
export function reportChunkError(error: unknown): void {
  if (current) void current.chunk.handle(error)
  else early.push(error)
}

let captureInstalled = false
/** Call once, before rendering: listens to `vite:preloadError` / unhandled import rejections from the start. */
export function installChunkCapture(): void {
  if (captureInstalled || !import.meta.env.PROD) return
  captureInstalled = true
  installChunkErrorListeners({ handle: async (error) => { reportChunkError(error); return 'ignored' } })
}

function openChannel(): ChannelLike | null {
  try {
    return typeof BroadcastChannel === 'undefined' ? null : (new BroadcastChannel('lumora-pwa') as unknown as ChannelLike)
  } catch {
    return null
  }
}

export function createPwaRuntime(queryClient: QueryClient): PwaRuntime {
  const reload = () => location.reload()
  const manager = createUpdateManager({
    currentBuild: APP_BUILD,
    storage: sessionStorage,
    channel: openChannel(),
    sw: createSwApi(),
    reload,
  })
  const onResult = (result: CheckResult) => {
    if (result.buildChanged && result.build) manager.onNewBuild(result.build, 'build')
    else if (result.contractNewer && result.contractVersion) manager.onNewBuild(`contract:${result.contractVersion}`, 'contract')
  }
  const checker = createVersionChecker({ check: () => checkVersion(), onResult })
  const chunk = createChunkErrorHandler({
    checkNow: () => checker.checkNow('manual'),
    flushDrafts: () => activity.flushDrafts(),
    setChunkError: manager.setChunkError,
    storage: sessionStorage,
    reload,
  })

  const runtime: PwaRuntime = {
    manager,
    checker,
    chunk,
    start() {
      current = runtime
      document.documentElement.dataset['appBuild'] = APP_BUILD // what is actually running (support / tests)
      const stops: (() => void)[] = []
      stops.push(watchIdentity(queryClient))
      void drafts.purgeExpired()
      try {
        sessionStorage.removeItem(reloadFlagKey(APP_BUILD)) // we are running the target now: the guard did its job
      } catch {
        /* ignore */
      }
      if (import.meta.env.PROD) {
        early.splice(0).forEach((error) => void chunk.handle(error))
        stops.push(checker.start())
        if (swSupported()) {
          void registerServiceWorker()
          // Another tab activated a new worker: find out which build that is and follow the normal path.
          const onControllerChange = () => {
            if (manager.getState().phase !== 'applying') void checker.checkNow('manual')
          }
          navigator.serviceWorker.addEventListener('controllerchange', onControllerChange)
          stops.push(() => navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange))
        }
      }
      return () => {
        stops.forEach((stop) => stop())
        if (current === runtime) current = null
      }
    },
  }
  return runtime
}
